import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

export interface CachedPhoto {
  url: string;
  blobUrl: string;
  size: number;
  cachedAt: Date;
  accessedAt: Date;
}

@Injectable({
  providedIn: 'root'
})
export class PhotoCacheService {
  private cache = new Map<string, CachedPhoto>();
  private readonly MAX_CACHE_SIZE = 50 * 1024 * 1024; // 50MB cache limit
  private readonly MAX_CACHE_ENTRIES = 100; // Maximum number of cached photos
  private currentCacheSize = 0;
  
  // Observable to track cache size for debugging
  private cacheSizeSubject = new BehaviorSubject<number>(0);
  public cacheSize$ = this.cacheSizeSubject.asObservable();

  constructor(private http: HttpClient) {
    // Clean up cache on page unload to prevent memory leaks
    window.addEventListener('beforeunload', () => {
      this.clearCache();
    });
  }

  /**
   * Get photo as blob URL, from cache if available, otherwise fetch and cache
   */
  getPhotoBlob(url: string): Observable<string> {
    if (!url || !url.trim()) {
      return of('');
    }

    const cached = this.cache.get(url);
    
    if (cached) {
      // Update access time for LRU cache management
      cached.accessedAt = new Date();
      return of(cached.blobUrl);
    }

    // Fetch photo and cache it
    return this.fetchAndCachePhoto(url);
  }

  /**
   * Check if photo is already cached
   */
  isCached(url: string): boolean {
    return this.cache.has(url);
  }

  /**
   * Preload photos in the background
   */
  preloadPhotos(urls: string[]): void {
    urls.forEach(url => {
      if (!this.isCached(url)) {
        this.fetchAndCachePhoto(url).subscribe({
          next: () => console.log(`Preloaded photo: ${url}`),
          error: (error) => console.warn(`Failed to preload photo: ${url}`, error)
        });
      }
    });
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; entries: number; maxSize: number; maxEntries: number } {
    return {
      size: this.currentCacheSize,
      entries: this.cache.size,
      maxSize: this.MAX_CACHE_SIZE,
      maxEntries: this.MAX_CACHE_ENTRIES
    };
  }

  /**
   * Clear entire cache
   */
  clearCache(): void {
    this.cache.forEach(cached => {
      URL.revokeObjectURL(cached.blobUrl);
    });
    this.cache.clear();
    this.currentCacheSize = 0;
    this.cacheSizeSubject.next(0);
    console.log('Photo cache cleared');
  }

  /**
   * Remove specific photo from cache
   */
  removeFromCache(url: string): void {
    const cached = this.cache.get(url);
    if (cached) {
      URL.revokeObjectURL(cached.blobUrl);
      this.currentCacheSize -= cached.size;
      this.cache.delete(url);
      this.cacheSizeSubject.next(this.currentCacheSize);
    }
  }

  private fetchAndCachePhoto(url: string): Observable<string> {
    return this.http.get(url, { responseType: 'blob' }).pipe(
      map(blob => {
        const blobUrl = URL.createObjectURL(blob);
        
        // Cache the photo
        const cached: CachedPhoto = {
          url,
          blobUrl,
          size: blob.size,
          cachedAt: new Date(),
          accessedAt: new Date()
        };

        // Check cache limits before adding
        this.ensureCacheSpace(blob.size);
        
        this.cache.set(url, cached);
        this.currentCacheSize += blob.size;
        this.cacheSizeSubject.next(this.currentCacheSize);
        
        console.log(`Cached photo: ${url} (${this.formatBytes(blob.size)})`);
        return blobUrl;
      }),
      catchError(error => {
        console.error(`Failed to fetch photo: ${url}`, error);
        // Return original URL as fallback
        return of(url);
      })
    );
  }

  private ensureCacheSpace(requiredSize: number): void {
    // If adding this photo would exceed cache limits, clean up old entries
    while (
      (this.currentCacheSize + requiredSize > this.MAX_CACHE_SIZE) ||
      (this.cache.size >= this.MAX_CACHE_ENTRIES)
    ) {
      this.removeOldestEntry();
    }
  }

  private removeOldestEntry(): void {
    if (this.cache.size === 0) return;

    // Find least recently used entry
    let oldestEntry: [string, CachedPhoto] | null = null;
    let oldestTime = new Date();

    for (const [url, cached] of this.cache.entries()) {
      if (cached.accessedAt < oldestTime) {
        oldestTime = cached.accessedAt;
        oldestEntry = [url, cached];
      }
    }

    if (oldestEntry) {
      const [url, cached] = oldestEntry;
      URL.revokeObjectURL(cached.blobUrl);
      this.currentCacheSize -= cached.size;
      this.cache.delete(url);
      console.log(`Removed from cache (LRU): ${url} (${this.formatBytes(cached.size)})`);
    }
  }

  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }
}